import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { Button } from 'Components/Buttons/Buttons';
import { Colors } from 'Components/Colors';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { PageVariant, Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { UsersModal } from 'Components/UcrmTprm/UsersModal/UsersModal';
import { ICON_ALERT_WARNING } from 'Config/Icons';
import { TPRM_ASSESSMENT, TPRM_ASSESSMENT_HISTORICAL, UCRM_ASSESSMENT, UCRM_ASSESSMENT_HISTORICAL, UCRM_QUESTIONNAIRE } from 'Config/PathTemplates';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { Effectiveness } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';
import { getQuestionnaireRespondentIds } from 'Models/Ucrm';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';
import { ControlsDashlet } from 'Pages/DueDiligenceAssessment/Components/HeaderDashlets/ControlsDashlet';
import { QuestionsDashlet } from 'Pages/DueDiligenceAssessment/Components/HeaderDashlets/QuestionsDashlet';
import { aggregateControlAssessmentStatistics, useControlAssessmentStatistics } from 'Pages/DueDiligenceAssessment/Hooks/useControlAssessmentStatistics';
import { aggregateQuestionsStatistics, useQuestionsStatistics } from 'Pages/DueDiligenceAssessment/Hooks/useQuestionsStatistics';

import styles from './AssessmentsListing.module.css';

/**
 * @param pageVariant The styling of the page the listing appears on. Passed through to the table and dashlets; on a `'dashboard'` page, text is white rather than blue.
 * @param authHandler Used to determine where each assessment's name links to in a current (not historical) UCRM listing. If omitted, every assessment's name links to its assessment page.
 * @param historical Set when listing the assessments of a completed (historical) due diligence workflow, identified by its archive's `timestamp`. Each assessment's name then links to its historical assessment page, and in UCRM, links are not tailored to the current user.
 */
export interface AssessmentsListingProps {
    assessments: TprmOrUcrmDueDiligenceAssessment[];
    module: DueDiligenceModule;
    pageVariant: PageVariant;

    authHandler?: AuthHandler;
    historical?: { timestamp: string };
}

/**
 * Renders a table of due diligence assessments for an assessment subject (TPRM third-party service or UCRM use case).
 * Used on the Service Dashboard page, the Use Case Dashboard page, and the Final Review page (for both current and historical final reviews).
 *
 * In UCRM, questionnaires are answered by SummIT users (respondents) rather than third-party users, and respondents may be different users than those who assess the controls. So for UCRM, each assessment's expanded row lists its respondents, and each assessment's name links the current user to the part of the assessment they are responsible for (see `getAssessmentPath`).
 */
export const AssessmentsListing = (props: AssessmentsListingProps) => {
    const cachedData = useCachedData();
    const questionsStatistics = useQuestionsStatistics(props.assessments);
    const controlAssessmentStatistics = useControlAssessmentStatistics(props.assessments);

    // Aggregate statistics across all assessments, shown in the summary dashlets above the table.
    const aggregateQuestionsStats = useMemo(() => aggregateQuestionsStatistics(Object.values(questionsStatistics)), [questionsStatistics]);
    const aggregateControlStats = useMemo(() => aggregateControlAssessmentStatistics(Object.values(controlAssessmentStatistics)), [controlAssessmentStatistics]);

    const [usersModal, setUsersModal] = useState<{ header: string; userIds: string[] }>();

    const textColor: Colors = props.pageVariant === 'dashboard' ? 'white' : 'blue';

    const currentUser = useMemo(() => {
        if (props.module !== ProductModule.UCRM || props.historical || !props.authHandler) {
            return undefined;
        }
        return { id: props.authHandler.getAuthenticatedUserSubject(), isAdmin: props.authHandler.getUserRoles().includes(Role.ADMIN) };
    }, [props.module, props.historical, props.authHandler]);

    /** Lists users inline, or links to a modal listing them if there are too many to display inline. */
    const renderUsers = (header: string, userIds: string[]) => {
        if (userIds.length === 0) {
            return (
                <div className={styles.matchLinkPadding}>
                    <Text noStyles color={textColor}>
                        -
                    </Text>
                </div>
            );
        } else if (userIds.length <= 5) {
            return (
                <div className={styles.matchLinkPadding}>
                    <Text noStyles color={textColor}>
                        {userIds
                            .map((userId) => getUserNameFromSubject(userId, cachedData.users, UserNameFormat.FIRST_SPACE_LAST))
                            .filter((name) => name !== undefined)
                            .join(', ')}
                    </Text>
                </div>
            );
        } else {
            return (
                <Button variant="linkText" onClick={() => setUsersModal({ header, userIds })} size="sm">
                    {`${userIds.length} ${header.toLowerCase()}`}
                </Button>
            );
        }
    };

    /**
     * The page that an assessment's name links to.
     * In a current UCRM listing, a questionnaire respondent who is not responsible for assessing the controls (as the assessment's owner or a delegate, or as an admin) is linked to the questionnaire; everyone else is linked to the assessment.
     */
    const getAssessmentPath = (assessment: TprmOrUcrmDueDiligenceAssessment): string => {
        const pathParams = { third_party_id: assessment.third_party_id, service_id: assessment.service_id, assessment_id: assessment.id };
        if (props.historical) {
            return generatePath(props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_HISTORICAL : TPRM_ASSESSMENT_HISTORICAL, { ...pathParams, timestamp: props.historical.timestamp });
        } else if (props.module === ProductModule.TPRM) {
            return generatePath(TPRM_ASSESSMENT, pathParams);
        }

        const userCanAssessControls = currentUser === undefined || currentUser.isAdmin || assessment.owner_id === currentUser.id || assessment.delegate_ids.includes(currentUser.id!);
        const userIsRespondent = currentUser !== undefined && getQuestionnaireRespondentIds(props.module, assessment).includes(currentUser.id!);
        return generatePath(!userCanAssessControls && userIsRespondent ? UCRM_QUESTIONNAIRE : UCRM_ASSESSMENT, pathParams);
    };

    const getExpandableElementForRow = (assessment: TprmOrUcrmDueDiligenceAssessment) => {
        return (
            <div className={styles.expandedContent}>
                <div className={styles.expandedRow}>
                    <div className={styles.expandedChartContainer}>
                        <div>
                            <Text variant="Header3" color={textColor}>
                                Questions
                            </Text>
                            <QuestionsDashlet pageVariant={props.pageVariant} statistics={questionsStatistics[assessment.id]} />
                        </div>
                        {props.module === ProductModule.UCRM && (
                            <div className={styles.expandedRow}>
                                <div>
                                    <Text variant="Header4" color={textColor}>
                                        Respondents
                                    </Text>
                                    {renderUsers('Respondents', getQuestionnaireRespondentIds(props.module, assessment))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={styles.expandedChartContainer}>
                        <div>
                            <Text variant="Header3" color={textColor}>
                                Controls
                            </Text>
                            <ControlsDashlet pageVariant={props.pageVariant} statistics={controlAssessmentStatistics[assessment.id]} />
                        </div>
                        <div className={styles.expandedRow}>
                            <div>
                                <Text variant="Header4" color={textColor}>
                                    Owner
                                </Text>
                                <div className={styles.matchLinkPadding}>
                                    <Text noStyles color={textColor}>
                                        {getUserNameFromSubject(assessment.owner_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}
                                    </Text>
                                </div>
                            </div>
                            <div>
                                <Text variant="Header4" color={textColor}>
                                    Delegates
                                </Text>
                                {renderUsers('Delegates', assessment.delegate_ids)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const columns: ColumnConfig<TprmOrUcrmDueDiligenceAssessment>[] = [
        {
            type: ColumnType.STRING,
            name: 'Name',
            getString: (assessment) => assessment.name,
            getOnClickTextAction: (assessment) => ({ goToLocation: getAssessmentPath(assessment) }),
        },
        {
            type: ColumnType.STRING,
            name: 'Status',
            getString: (assessment) =>
                (() => {
                    if (assessment.assessment_submitted_timestamp) {
                        return 'Complete';
                    } else if (assessment.questionnaire_submitted_timestamp) {
                        return 'Questionnaire submitted';
                    } else {
                        return 'Questionnaire in progress';
                    }
                })(),
            getIcon: (assessment) => (questionsStatistics[assessment.id].unacceptable > 0 ? <FontAwesomeIcon className={styles.red} icon={ICON_ALERT_WARNING} size="lg" title={`${questionsStatistics[assessment.id].unacceptable} unacceptable ${questionsStatistics[assessment.id].unacceptable === 1 ? 'answer' : 'answers'}`} /> : undefined),
        },
        {
            type: ColumnType.DATE,
            name: 'Questionnaire Due Date',
            getDateString: (item) => item.questionnaire_due_date,
            getIcon: (assessment) => (assessment.questionnaire_due_date && assessment.questionnaire_submitted_timestamp === undefined && moment(assessment.questionnaire_due_date).isBefore() ? <FontAwesomeIcon className={styles.orange} icon={ICON_ALERT_WARNING} size="lg" title="Overdue" /> : undefined),
        },
        {
            type: ColumnType.DATE,
            name: 'Assessment Due Date',
            getDateString: (assessment) => assessment.assessment_due_date,
            getIcon: (assessment) => (assessment.assessment_due_date && assessment.assessment_submitted_timestamp === undefined && moment(assessment.assessment_due_date).isBefore() ? <FontAwesomeIcon className={styles.orange} icon={ICON_ALERT_WARNING} size="lg" title="Overdue" /> : undefined),
        },
        {
            type: ColumnType.CONTROL_EFFECTIVENESS,
            name: 'Overall Control Effectiveness',
            // Do not display overall effectiveness at the "summary" level unless the assessment has been submitted.
            getControlEffectiveness: (assessment) => (assessment.assessment_submitted_timestamp ? assessment.assessment_review_human.effectiveness : Effectiveness.UNRATED) ?? Effectiveness.UNRATED,
        },
        {
            type: ColumnType.RISK_RATING,
            name: 'Residual Risk',
            // Do not display residual risk at the "summary" level unless the assessment has been submitted.
            getRiskRating: (assessment) => (assessment.assessment_submitted_timestamp ? assessment.assessment_review_human.residual_risk : RiskRating.UNRATED) ?? RiskRating.UNRATED,
        },
    ];

    return (
        <>
            {usersModal && <UsersModal header={usersModal.header} userIds={usersModal.userIds} hideModal={() => setUsersModal(undefined)} />}
            {props.assessments.length > 0 && (
                <div className={styles.summaryDashlets}>
                    <div className={styles.summaryDashletColumn}>
                        <Text variant="Header3" color={textColor}>
                            Questions
                        </Text>
                        <QuestionsDashlet pageVariant={props.pageVariant} statistics={aggregateQuestionsStats} />
                    </div>
                    <div className={styles.summaryDashletColumn}>
                        <Text variant="Header3" color={textColor}>
                            Controls
                        </Text>
                        <ControlsDashlet pageVariant={props.pageVariant} statistics={aggregateControlStats} />
                    </div>
                </div>
            )}
            <Table pageVariant={props.pageVariant} columns={columns} defaultSortColumn="Name" filters={[]} getUniqueIdForRow={(assessment) => assessment.id} rowsData={props.assessments} getExpandableElementForRow={getExpandableElementForRow} />
        </>
    );
};
